import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatAddress,
  toLocalDateKey,
  type Customer,
  type Job,
  type JobPriority,
  type LineItemInput,
} from '@fsm/shared';
import { api } from '../lib/api';
import { invalidateJobWorld, keys, useTechs } from '../lib/queries';
import { LineItemEditor } from './LineItemEditor';
import { NewCustomerModal } from './NewCustomerModal';
import { Button, Field, Input, Modal, Select, Textarea } from './ui';
import { useAuth } from '../lib/auth';

export function NewJobModal({
  open,
  onClose,
  onCreated,
  defaultDay,
  defaultCustomerId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (job: Job) => void;
  defaultDay?: Date;
  defaultCustomerId?: string;
}) {
  const qc = useQueryClient();
  const { company } = useAuth();
  const { data: techs = [] } = useTechs();

  const [search, setSearch] = useState('');
  const [customerId, setCustomerId] = useState(defaultCustomerId ?? '');
  const [propertyId, setPropertyId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<JobPriority>('normal');
  const [techId, setTechId] = useState('');
  const [day, setDay] = useState(() => toLocalDateKey(defaultDay ?? new Date()));
  const [startTime, setStartTime] = useState('09:00');
  const [durationMins, setDurationMins] = useState(120);
  const [lineItems, setLineItems] = useState<LineItemInput[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [addingCustomer, setAddingCustomer] = useState(false);

  const { data: customers = [] } = useQuery({
    queryKey: keys.customers(search),
    queryFn: () => api.customers.list({ q: search || undefined, limit: 25 }),
    select: (d) => d.items,
    enabled: open,
  });

  /*
   * The customer this job was started from, fetched by id.
   *
   * Not found in `customers` above, and that was the bug. That list is the
   * search result — twenty-five rows matching whatever is in the search box —
   * so opening "New job" from a customer's own page only pre-filled them if
   * they happened to be in the first twenty-five customers alphabetically.
   * For everyone else the Select had no option matching the id, rendered
   * blank, and the address stayed disabled behind "pick a customer first" on
   * a screen that already knew exactly who the customer was.
   */
  const { data: namedCustomer } = useQuery({
    queryKey: keys.customer(defaultCustomerId ?? ''),
    queryFn: () => api.customers.get(defaultCustomerId!),
    enabled: open && Boolean(defaultCustomerId),
  });

  const selected =
    customers.find((c: Customer) => c.id === customerId) ??
    (namedCustomer?.id === customerId ? namedCustomer : undefined);

  // Default to the customer's primary address as soon as one is chosen.
  useEffect(() => {
    if (!selected?.properties?.length) return;
    if (selected.properties.some((p) => p.id === propertyId)) return;
    setPropertyId(selected.properties[0]!.id);
  }, [selected, propertyId]);

  useEffect(() => {
    if (open && defaultDay) setDay(toLocalDateKey(defaultDay));
  }, [open, defaultDay]);

  /*
   * On every open, not only the first.
   *
   * `useState(defaultCustomerId ?? '')` runs once, when the modal mounts —
   * which is while it is still closed, and before `onCreated` has reset
   * anything. Booking a second job for the same customer would otherwise open
   * an empty picker.
   */
  useEffect(() => {
    if (open && defaultCustomerId) setCustomerId(defaultCustomerId);
  }, [open, defaultCustomerId]);

  const window = useMemo(() => {
    if (!techId) return null;
    const [hh, mm] = startTime.split(':').map(Number);
    const start = new Date(`${day}T00:00:00`);
    start.setHours(hh ?? 9, mm ?? 0, 0, 0);
    return { start, end: new Date(start.getTime() + durationMins * 60_000) };
  }, [techId, day, startTime, durationMins]);

  const create = useMutation({
    mutationFn: () =>
      api.jobs.create({
        title: title.trim(),
        description: description.trim() || null,
        customerId,
        propertyId,
        priority,
        assignedTechId: techId || null,
        scheduledStart: window?.start.toISOString() ?? null,
        scheduledEnd: window?.end.toISOString() ?? null,
        lineItems,
      }),
    onSuccess: (job) => {
      invalidateJobWorld(qc);
      reset();
      onCreated(job);
    },
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not create the job'),
  });

  function reset() {
    setTitle('');
    setDescription('');
    setLineItems([]);
    setTechId('');
    setError(null);
  }

  const canSubmit = title.trim().length > 0 && customerId && propertyId;

  /*
   * A customer created from here is used immediately rather than just added to
   * the list. The create endpoint returns the customer with its properties, so
   * the address it was given is already in hand — selecting it here is what
   * stops the dispatcher having to find the person they just typed.
   */
  function adoptCustomer(customer: Customer) {
    setAddingCustomer(false);
    qc.setQueryData(keys.customers(search), (prev: { items: Customer[] } | undefined) =>
      prev ? { ...prev, items: [customer, ...prev.items] } : prev,
    );
    void qc.invalidateQueries({ queryKey: ['customers'] });
    setCustomerId(customer.id);
    setPropertyId(customer.properties?.[0]?.id ?? '');
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      title="New job"
      description="Leave the technician blank to drop this straight into the backlog."
      width="xl"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Create job
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Customer" required>
            {/*
              * Started from a customer's own page, the customer is not a
              * question. Showing a search box and a dropdown there asks
              * somebody to re-answer something the screen already knows, and
              * offers them the chance to get it wrong.
              */}
            {defaultCustomerId ? (
              <div className="flex min-h-[40px] items-center rounded-lg border border-line bg-raised px-3 text-[14px] font-semibold text-ink">
                {selected?.name ?? 'Loading…'}
              </div>
            ) : (
              <>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, phone or address"
                  className="mb-1.5"
                />
                <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">Choose a customer…</option>
                  {customers.map((c: Customer) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                {/*
                  * Always offered, not only when the search comes up empty: a
                  * dispatcher on the phone with somebody new does not search
                  * first to prove they are new. The wording changes, though,
                  * because a search that found nothing is worth acknowledging.
                  */}
                <button
                  type="button"
                  onClick={() => setAddingCustomer(true)}
                  className="mt-1.5 text-[12px] font-semibold text-accent hover:underline"
                >
                  {search.trim() && customers.length === 0
                    ? `+ Add "${search.trim()}" as a new customer`
                    : '+ New customer'}
                </button>
              </>
            )}
          </Field>

          <Field
            label="Address"
            required
            hint={selected || defaultCustomerId ? undefined : '— pick a customer first'}
          >
            <Select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              disabled={!selected}
              /*
               * The offset clears the search box that sits above the customer
               * Select. With the customer fixed there is no search box, so the
               * two columns line up on their own.
               */
              className={defaultCustomerId ? undefined : 'mt-[38px]'}
            >
              {(selected?.properties ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label ? `${p.label} — ` : ''}
                  {formatAddress(p)}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="What's the job?" required>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="AC not cooling — diagnostic"
          />
        </Field>

        <Field label="Details" hint="optional">
          <Textarea
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the customer said on the phone"
          />
        </Field>

        <div className="grid grid-cols-4 gap-3">
          <Field label="Priority">
            <Select value={priority} onChange={(e) => setPriority(e.target.value as JobPriority)}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="emergency">Emergency</option>
            </Select>
          </Field>
          <Field label="Technician" hint="optional">
            <Select value={techId} onChange={(e) => setTechId(e.target.value)}>
              <option value="">Backlog</option>
              {techs.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Day">
            <Input
              type="date"
              value={day}
              disabled={!techId}
              onChange={(e) => setDay(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Start">
              <Input
                type="time"
                value={startTime}
                disabled={!techId}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </Field>
            <Field label="Hours">
              <Select
                value={durationMins}
                disabled={!techId}
                onChange={(e) => setDurationMins(Number(e.target.value))}
              >
                {[30, 60, 90, 120, 180, 240, 360, 480].map((m) => (
                  <option key={m} value={m}>
                    {m < 60 ? `${m}m` : `${m / 60}h`}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </div>

        <div className="rounded-lg border border-line p-3">
          <h3 className="mb-2 text-[11px] font-semibold tracking-[0.15em] text-ink-4 uppercase">
            Line items
          </h3>
          <LineItemEditor
            value={lineItems}
            onChange={setLineItems}
            taxRateBps={company?.taxRateBps ?? 0}
          />
        </div>

        {error && (
          <p className="rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">{error}</p>
        )}
      </div>
    </Modal>

    {/* A sibling rather than a child, so it paints over the job form. */}
    <NewCustomerModal
      open={addingCustomer}
      onClose={() => setAddingCustomer(false)}
      onCreated={adoptCustomer}
      requireAddress
      defaultName={search}
    />
    </>
  );
}
